import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Rating
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  Legend, ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts';
import { format } from 'date-fns';
import AdminService from '../../services/AdminService';

// Simple Bar Chart component
const BarChart_ = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <RechartsTooltip />
        <Bar dataKey="value" fill="#8884d8" />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Pie Chart component
const PieChart_ = ({ data }) => {
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
  
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <RechartsTooltip />
      </PieChart>
    </ResponsiveContainer>
  );
};

// Create a component to display feedback distribution
const FeedbackDistribution = ({ distribution }) => {
  if (!distribution || Object.keys(distribution).length === 0) return null;
  
  const data = [
    { name: 'Positive', value: distribution.positive || 0 },
    { name: 'Negative', value: distribution.negative || 0 }
  ];
  
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Feedback Distribution
      </Typography>
      <Box sx={{ height: 300, mt: 2 }}>
        <PieChart_ data={data} />
      </Box>
    </Paper>
  );
};

// Create a component to display rating distribution
const RatingDistribution = ({ ratings }) => {
  // Ensure ratings is always an array
  const ratingsArray = Array.isArray(ratings) ? ratings : [];
  
  if (ratingsArray.length === 0) return null;
  
  // Convert ratings to chart data format
  const data = Array.from({ length: 5 }, (_, i) => ({
    name: `${i + 1} Star${i === 0 ? '' : 's'}`,
    value: ratingsArray.filter(r => r === i + 1).length
  }));
  
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Rating Distribution
      </Typography>
      <Box sx={{ height: 300, mt: 2 }}>
        <BarChart_ data={data} />
      </Box>
    </Paper>
  );
};

// Create a top questions component
const TopQuestions = ({ topQuestions }) => {
  if (!topQuestions || topQuestions.length === 0) return null;
  
  const chartData = topQuestions.slice(0, 5).map(item => ({
    name: item.question ? (item.question.length > 30 ? item.question.substring(0, 30) + '...' : item.question) : 'Unknown',
    value: item.count || 0
  }));
  
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Top Questions Receiving Feedback
      </Typography>
      <Box sx={{ height: 300, mt: 2 }}>
        <BarChart_ data={chartData} />
      </Box>
    </Paper>
  );
};

// Create a feedback list component
const FeedbackList = ({ 
  feedback, 
  total, 
  page, 
  rowsPerPage, 
  handleChangePage, 
  handleChangeRowsPerPage,
  handleRefresh,
  handleViewFeedback
}) => {
  // Ensure feedback is always an array even if it's undefined
  const feedbackItems = Array.isArray(feedback) ? feedback : [];
  
  return (
    <Paper sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Typography variant="h6">
          Feedback Entries
        </Typography>
        
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>
      
      <TableContainer>
        <Table size="small">
        <TableHead>
            <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Session ID</TableCell>
                <TableCell>Session User</TableCell>
              <TableCell>Question</TableCell>
              <TableCell>Response</TableCell>
              <TableCell>Feedback Type</TableCell>
              <TableCell>Feedback Category</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feedbackItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No feedback entries found
                </TableCell>
              </TableRow>
            ) : (
              feedbackItems.map((entry) => (
                <TableRow key={entry.id || `row-${Math.random()}`} hover>
                  <TableCell>
                    {entry.timestamp ? format(new Date(entry.timestamp), 'yyyy-MM-dd HH:mm') : 'N/A'}
                  </TableCell>
                  <TableCell>{entry.session_id || 'Unknown'}</TableCell>
                  <TableCell>{entry.session_user || 'Anonymous'}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap>
                      {entry.question || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" noWrap>
                      {entry.response ? (entry.response.length > 50 ? entry.response.substring(0, 50) + '...' : entry.response) : 'N/A'}
                    </Typography>
                  </TableCell>                  
                  <TableCell>
                    <Chip 
                      label={entry.feedback_type || 'Unspecified'} 
                      color={
                        entry.feedback_type === 'Like' ? 'success' : 
                        entry.feedback_type === 'Dislike' ? 'error' : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={entry.feedback_category || 'Unspecified'} 
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={() => handleViewFeedback(entry)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={total || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

// Main FeedbackAnalytics component
const FeedbackAnalytics = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [feedbackData, setFeedbackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination for feedback entries
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [feedbackType, setFeedbackType] = useState('');
  const [feedbackCategory, setFeedbackCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const fetchFeedbackData = async (customFilters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = {
        page: customFilters.page !== undefined ? customFilters.page : page + 1, // API uses 1-based pagination
        limit: customFilters.limit !== undefined ? customFilters.limit : rowsPerPage,
        ...customFilters
      };
      
      // Add date filters if they exist
      if (startDate) {
        filters.start_date = format(startDate, 'yyyy-MM-dd');
      }
      
      if (endDate) {
        filters.end_date = format(endDate, 'yyyy-MM-dd');
      }
      
      // Add type filter if it exists
      if (feedbackType) {
        filters.feedback_type = feedbackType;
      }
      
      // Add category filter if it exists
      if (feedbackCategory) {
        filters.feedback_category = feedbackCategory;
      }
      
      // Add search query if it exists - FIXED: use search_query to match AdminService
      if (searchQuery) {
        filters.search_query = searchQuery;
      }
      
      // FIXED: Calculate offset from page for AdminService API
      filters.offset = (filters.page - 1) * filters.limit;
      
      try {
        let response;
        
        // Fetch different data based on active tab
        if (activeTab === 1) {
          // For overview tab, get analytics data
          response = await AdminService.getFeedbackAnalytics(filters);
        } else {
          // For entries tab, get detailed entries
          response = await AdminService.getFeedbackEntries(filters);
        }
        
        if (response) {
          setFeedbackData(response);
        } else {
          // If no data is found, set empty data
          setFeedbackData({
            distribution: { positive: 0, neutral: 0, negative: 0 },
            ratings: [],
            topQuestions: [],
            feedback: [],
            total: 0
          });
        }      } catch (apiError) {
        console.error('API error fetching feedback:', apiError);
        
        // If API error occurs, set an error state and empty data
        setError(`Failed to fetch feedback data: ${apiError.message || 'Unknown error'}`);
        
        // Set empty data structure
        setFeedbackData({
          distribution: { positive: 0, neutral: 0, negative: 0 },
          ratings: [],
          topQuestions: [],
          feedback: [],
          total: 0
        });
      }
    } catch (err) {
      console.error('Error fetching feedback data:', err);
      setError('Failed to load feedback data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // FIXED: Use useCallback to memoize the function and prevent infinite re-renders
  const fetchFeedbackDataCallback = useCallback(() => {
    fetchFeedbackData();
  }, [activeTab, page, rowsPerPage, startDate, endDate, feedbackType, feedbackCategory, searchQuery]);
  
  useEffect(() => {
    fetchFeedbackDataCallback();
  }, [fetchFeedbackDataCallback]);
  
  const handleRefresh = () => {
    fetchFeedbackDataCallback();
  };
  
  const handleViewFeedback = (entry) => {
    console.log('View Feedback Entry:', entry);
    setSelectedFeedback(entry);
    setViewDialogOpen(true);
  };
    const handleCloseDialog = () => {
    setViewDialogOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleSearch = () => {
    setPage(0);
    fetchFeedbackDataCallback();
  };
  
  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setFeedbackType('');
    setFeedbackCategory('');
    setSearchQuery('');
    setPage(0);
    fetchFeedbackDataCallback();
  };
    const handleExportFeedback = async () => {
  try {
    setLoading(true);
    setError(null);
    
    const exportFilters = {};
    
    // Add current filters to export
    if (startDate) {
      exportFilters.start_date = format(startDate, 'yyyy-MM-dd');
    }
    
    if (endDate) {
      exportFilters.end_date = format(endDate, 'yyyy-MM-dd');
    }
    
    if (feedbackType) {
      exportFilters.feedback_type = feedbackType;
    }
    
    if (feedbackCategory) {
      exportFilters.feedback_category = feedbackCategory;
    }
    
    if (searchQuery) {
      exportFilters.search_query = searchQuery;
    }
    
    // The AdminService.exportFeedback method now handles pagination to get all entries
    await AdminService.exportFeedback(exportFilters);
    
    // Show success message
    setSnackbarMessage('Feedback exported successfully!');
    setSnackbarOpen(true);
    
  } catch (error) {
    console.error('Error exporting feedback:', error);
    setError('Failed to export feedback. Please try again.');
  } finally {
    setLoading(false);
  }
};
  
  return (
    <Box>
      {/* <Typography variant="h5" sx={{ mb: 3 }}>Feedback Analytics</Typography> */}
      
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        
      </Tabs>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    margin: 'dense'
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    margin: 'dense'
                  },
                }}
              />
            </Grid>
          </LocalizationProvider>
          
          <Grid item xs={12} sm={12} md={2}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel>Feedback Type</InputLabel>
              <Select
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value)}
                label="Feedback Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Like">Like</MenuItem>
                <MenuItem value="Dislike">Dislike</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel>Feedback Category</InputLabel>
              <Select
                value={feedbackCategory}
                onChange={(e) => setFeedbackCategory(e.target.value)}
                label="Feedback Category"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="improvement">Suggest an improvement</MenuItem>
                <MenuItem value="question">Ask a question</MenuItem>
                <MenuItem value="bug">Report a bug</MenuItem>
              </Select>
            </FormControl>
          </Grid> */}
          
          {activeTab === 0 && (
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
                margin="dense"
              />
            </Grid>
          )}
          
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{ flexGrow: 1 }}
              >
                Apply
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            </Box>
          </Grid>
          
          {activeTab === 0 && (
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportFeedback}
                disabled={!feedbackData || !feedbackData.feedback || feedbackData.feedback.length === 0}
              >
                Export CSV
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === 1 && feedbackData && (
            // Overview tab content
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FeedbackDistribution distribution={feedbackData.distribution} />
              </Grid>
              <Grid item xs={12} md={6}>
                <RatingDistribution ratings={feedbackData.ratings} />
              </Grid>
              <Grid item xs={12}>
                <TopQuestions topQuestions={feedbackData.topQuestions} />
              </Grid>
            </Grid>
          )}
          
          {activeTab === 0 && feedbackData && (
            // Entries tab content            
            <FeedbackList 
              feedback={feedbackData.feedback}
              total={feedbackData.total}
              page={page}
              rowsPerPage={rowsPerPage}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleRefresh={handleRefresh}
              handleViewFeedback={handleViewFeedback}
            />
          )}        
        </>
      )}
      
      {/* Feedback Detail Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Feedback Details
        </DialogTitle>
        <DialogContent dividers>
          {selectedFeedback && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Session ID</Typography>
                <Typography variant="body2">{selectedFeedback.session_id || 'Anonymous'}</Typography>
                
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Timestamp</Typography>
                <Typography variant="body2">
                  {selectedFeedback.timestamp ? format(new Date(selectedFeedback.timestamp), 'yyyy-MM-dd HH:mm') : 'N/A'}
                </Typography>
                
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Feedback Type</Typography>
                <Chip 
                  label={selectedFeedback.feedback_type || 'Unspecified'} 
                  color={
                    selectedFeedback.feedback_type === 'Like' ? 'success' : 
                    selectedFeedback.feedback_type === 'Dislike' ? 'error' : 'default'
                  }
                  size="small"
                />
                
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Feedback Category</Typography>
                <Chip 
                  label={selectedFeedback.feedback_category || 'Unspecified'} 
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Question</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedFeedback.question || 'N/A'}
                  </Typography>
                </Paper>
                
                <Typography variant="subtitle2" gutterBottom>Response</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedFeedback.response || 'N/A'}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Feedback Comments</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.lightest', minHeight: '100px' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedFeedback.comments || selectedFeedback.feedback || 'No comments provided'}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>        </DialogActions>
      </Dialog>
      
      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </Box>
  );
};

export default FeedbackAnalytics;